import { useCallback, useMemo, useState, useRef, useEffect } from "react";
import { ReactFlow, Background, Controls, type Node, type Edge, type NodeChange, type EdgeChange, type Connection, type ReactFlowInstance, applyNodeChanges, applyEdgeChanges } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { StartNode, AgentNode, DecisionNode, ActionNode, EndNode } from "./nodes";
import type { WorkflowNode, WorkflowEdge } from "@/types/workflow";

interface WorkflowViewerProps {
    nodes: WorkflowNode[];
    edges: WorkflowEdge[];
    workflowId?: string;
    onNodesPositionChange?: (nodes: WorkflowNode[]) => void;
    onEdgesChange?: (edges: WorkflowEdge[]) => void;
    onSaveReady?: (onSave: () => void) => void;
}

const nodeTypes = {
    start: StartNode,
    agent: AgentNode,
    decision: DecisionNode,
    action: ActionNode,
    end: EndNode,
};

export const WorkflowViewer = ({ nodes, edges, workflowId, onNodesPositionChange, onEdgesChange, onSaveReady }: WorkflowViewerProps) => {
    const rfInstanceRef = useRef<ReactFlowInstance | null>(null);

    // Local state for nodes (to allow dragging)
    const [localNodes, setLocalNodes] = useState<Node[]>(
        nodes.map((node) => ({
            id: node.id,
            type: node.type,
            position: node.position,
            data: node.data,
            draggable: true, // Ensure all nodes are draggable
        }))
    );

    // Local state for edges (to allow handle modifications)
    const [localEdges, setLocalEdges] = useState<Edge[]>(
        edges.map((edge) => ({
            id: edge.id,
            source: edge.source,
            target: edge.target,
            sourceHandle: edge.sourceHandle,
            targetHandle: edge.targetHandle,
            label: edge.label,
            style: { stroke: "#6366f1", strokeWidth: 2 },
            reconnectable: true, // Allow edge handle reconnection
        }))
    );

    // Update local nodes when props change
    useMemo(() => {
        setLocalNodes(
            nodes.map((node) => ({
                id: node.id,
                type: node.type,
                position: node.position,
                data: node.data,
                draggable: true, // Ensure all nodes are draggable
            }))
        );
    }, [nodes]);

    // Update local edges when props change
    useMemo(() => {
        setLocalEdges(
            edges.map((edge) => ({
                id: edge.id,
                source: edge.source,
                target: edge.target,
                sourceHandle: edge.sourceHandle,
                targetHandle: edge.targetHandle,
                label: edge.label,
                style: { stroke: "#6366f1", strokeWidth: 2 },
                reconnectable: true, // Allow edge handle reconnection
            }))
        );
    }, [edges]);

    // Handle node changes (position updates only - no immediate save)
    const onNodesChange = useCallback(
        (changes: NodeChange[]) => {
            const updatedNodes = applyNodeChanges(changes, localNodes);
            setLocalNodes(updatedNodes);

            // If position changed, notify parent (debounced save will happen there)
            const positionChanges = changes.filter(
                (change) => change.type === "position" && change.position !== undefined
            );

            if (positionChanges.length > 0 && workflowId && onNodesPositionChange) {
                // Convert back to WorkflowNode format with updated positions
                const updatedWorkflowNodes: WorkflowNode[] = updatedNodes.map((node) => {
                    const originalNode = nodes.find((n) => n.id === node.id);
                    if (!originalNode) {
                        throw new Error(`Node ${node.id} not found in original nodes`);
                    }
                    // Preserve all original data, only update position
                    return {
                        id: originalNode.id,
                        type: originalNode.type,
                        position: node.position, // Updated position
                        data: originalNode.data, // Preserve original data
                    };
                });
                onNodesPositionChange(updatedWorkflowNodes);
            }
        },
        [localNodes, nodes, workflowId, onNodesPositionChange]
    );

    // Handle edge changes (only sourceHandle/targetHandle modifications, no deletion)
    const handleEdgesChange = useCallback(
        (changes: EdgeChange[]) => {
            // Block deletions in read-only mode
            const hasDeletions = changes.some((change) => change.type === "remove");
            if (hasDeletions) {
                return; // Ignore deletion attempts
            }

            const updatedEdges = applyEdgeChanges(changes, localEdges);
            setLocalEdges(updatedEdges);

            // Check if any edge actually changed (sourceHandle or targetHandle)
            const hasHandleChanges = updatedEdges.some((edge) => {
                const originalEdge = edges.find((e) => e.id === edge.id);
                if (!originalEdge) return false;
                return (
                    edge.sourceHandle !== originalEdge.sourceHandle ||
                    edge.targetHandle !== originalEdge.targetHandle
                );
            });

            if (hasHandleChanges && workflowId && onEdgesChange) {
                // Convert back to WorkflowEdge format
                const updatedWorkflowEdges: WorkflowEdge[] = updatedEdges.map((edge) => {
                    const originalEdge = edges.find((e) => e.id === edge.id);
                    if (!originalEdge) {
                        throw new Error(`Edge ${edge.id} not found in original edges`);
                    }
                    return {
                        id: originalEdge.id,
                        source: originalEdge.source,
                        target: originalEdge.target,
                        sourceHandle: edge.sourceHandle ?? undefined,
                        targetHandle: edge.targetHandle ?? undefined,
                        label: originalEdge.label,
                    };
                });
                onEdgesChange(updatedWorkflowEdges);
            }
        },
        [localEdges, edges, workflowId, onEdgesChange]
    );

    // Handle edge reconnection (when user drags edge endpoint to different handle)
    const onReconnect = useCallback(
        (oldEdge: Edge, newConnection: Connection) => {
            if (!workflowId || !onEdgesChange) return;

            // Update the edge with new connection handles
            const updatedEdges = localEdges.map((edge) => {
                if (edge.id === oldEdge.id) {
                    return {
                        ...edge,
                        source: newConnection.source || edge.source,
                        target: newConnection.target || edge.target,
                        sourceHandle: newConnection.sourceHandle ?? edge.sourceHandle,
                        targetHandle: newConnection.targetHandle ?? edge.targetHandle,
                    };
                }
                return edge;
            });

            setLocalEdges(updatedEdges);

            // Convert to WorkflowEdge format and notify parent
            const updatedWorkflowEdges: WorkflowEdge[] = updatedEdges.map((edge) => {
                const originalEdge = edges.find((e) => e.id === edge.id);
                if (!originalEdge) {
                    throw new Error(`Edge ${edge.id} not found in original edges`);
                }
                return {
                    id: originalEdge.id,
                    source: edge.source,
                    target: edge.target,
                    sourceHandle: edge.sourceHandle ?? undefined,
                    targetHandle: edge.targetHandle ?? undefined,
                    label: originalEdge.label,
                };
            });

            onEdgesChange(updatedWorkflowEdges);
        },
        [localEdges, edges, workflowId, onEdgesChange]
    );

    // Save function to localStorage
    const onSave = useCallback(() => {
        if (rfInstanceRef.current && workflowId) {
            const flow = rfInstanceRef.current.toObject();
            const flowKey = `workflow-${workflowId}`;
            localStorage.setItem(flowKey, JSON.stringify(flow));
        }
    }, [workflowId]);

    // Expose onSave function to parent via callback
    useEffect(() => {
        if (onSaveReady) {
            onSaveReady(onSave);
        }
    }, [onSaveReady, onSave]);

    // Initialize ReactFlow instance
    const onInit = useCallback((instance: ReactFlowInstance) => {
        rfInstanceRef.current = instance;
    }, []);

    return (
        <div className="w-full h-full">
            <ReactFlow
                nodes={localNodes}
                edges={localEdges}
                onInit={onInit}
                onNodesChange={onNodesChange}
                onEdgesChange={handleEdgesChange}
                onReconnect={onReconnect}
                nodeTypes={nodeTypes}
                nodesDraggable={true}
                nodesConnectable={false}
                edgesReconnectable={true}
                elementsSelectable={true}
                panOnDrag={false} // Disable pan on drag to allow node dragging
                panOnScroll={true} // Allow panning with scroll wheel + modifier
                zoomOnScroll={true}
                zoomOnPinch={true}
                fitView
            >
                <Background />
                <Controls />
            </ReactFlow>
        </div>
    );
};
